import React, { FC, useState, useEffect } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Icon,
  SimpleGrid,
  Progress,
  Badge,
  Heading,
} from '@chakra-ui/react';
import {
  HomeIcon,
  MessageCircleIcon,
  BarChart2Icon,
  Share2Icon,
  FileTextIcon,
  PlayIcon,
  ActivityIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  BrainIcon,
  UsersIcon,
  TrendingUpIcon,
  SettingsIcon,
  UserIcon,
  HelpCircleIcon,
  InfoIcon,
  ListIcon,
  MenuIcon,
  NetworkIcon,
  ShieldIcon,
  HeartIcon,
  CalendarIcon,
  ClockIcon,
  GitBranchIcon,
  StarIcon,
  BuildingIcon,
  Zap as ZapIcon,
} from 'lucide-react';
import MobileFrame from './MobileFrame';
import MobileResilienceHome from './MobileResilienceHome';
import MobileResilienceActivities from './MobileResilienceActivities';
import MobileResilienceActivityDetail from './MobileResilienceActivityDetail';
import MobileResilienceMetrics from './MobileResilienceMetrics';
import MobileResilienceAssistant from './MobileResilienceAssistant';
import MobileCultureHome from './MobileCultureHome';
import MobileCultureActivities from './MobileCultureActivities';
import MobileCultureMetrics from './MobileCultureMetrics';
import MobileCultureAssistant from './MobileCultureAssistant';
import MobileCultureWellness from './MobileCultureWellness';
import MobileCultureEventDetail from './MobileCultureEventDetail';
import MobileMenu from './MobileMenu';
import UnifiedHomePage from './UnifiedHomePage';

interface UnifiedMobilePlatformProps {
  activeDomain: string;
  defaultTab?: string;
  highlightedFeature?: string;
}

const methodistColors = {
  blue: '#0066CC',
  navy: '#002D72',
  green: '#38A169',
  purple: '#805AD5',
};

// Methodist color palette from design system

export type DomainType = 'AQ' | 'CQ' | 'WQ';

interface Metric {
  label: string;
  value: number;
  goal: number;
  icon: any;
  color: string;
  progress: number;
}

type HighlightType = {
  label: string;
  value: string;
  icon: any;
  progress: number;
  goal: number;
}

type GoalType = {
  label: string;
  current: number;
  target: number;
}

type DomainMetrics = {
  score: number;
  trend: string;
  metrics: Metric[];
  highlights: HighlightType[];
  weeklyGoal: GoalType;
  monthlyGoal: GoalType;
}

const domainTitles = {
  AQ: 'SentientIQ Adaptability Quotient',
  CQ: 'SentientIQ Cultural Quotient',
  WQ: 'SentientIQ Workplace Quotient',
};

const domainMetrics: { [key in DomainType]: DomainMetrics } = {
  AQ: {
    score: 85,
    trend: '+5',
    metrics: [
      {
        label: 'Stress Resilience',
        value: 85,
        goal: 90,
        icon: ShieldIcon,
        color: methodistColors.blue,
        progress: 85,
      },
      {
        label: 'Mental Focus',
        value: 78,
        goal: 85,
        icon: BrainIcon,
        color: methodistColors.purple,
        progress: 78,
      },
      {
        label: 'Recovery Rate',
        value: 92,
        goal: 95,
        icon: TrendingUpIcon,
        color: methodistColors.green,
        progress: 92,
      }
    ],
    highlights: [
      { label: 'Scheduled Breaks', value: '8/10', icon: CalendarIcon, progress: 80, goal: 10 },
      { label: 'Deep Work Sessions', value: '12/15', icon: CheckCircleIcon, progress: 80, goal: 15 },
      { label: 'Team Check-ins', value: '5/5', icon: UsersIcon, progress: 100, goal: 5 }
    ],
    weeklyGoal: { label: 'Days Active', current: 5, target: 7 },
    monthlyGoal: { label: 'Target Score', current: 85, target: 90 }
  },
  CQ: {
    score: 92,
    trend: '+3',
    metrics: [
      {
        label: 'Cultural Awareness',
        value: 90,
        goal: 95,
        icon: UsersIcon,
        color: methodistColors.blue,
        progress: 90,
      },
      {
        label: 'Empathy Score',
        value: 88,
        goal: 90,
        icon: HeartIcon,
        color: methodistColors.purple,
        progress: 88,
      },
      {
        label: 'Team Dynamics',
        value: 94,
        goal: 95,
        icon: NetworkIcon,
        color: methodistColors.green,
        progress: 94,
      }
    ],
    highlights: [
      { label: 'Cultural Sessions', value: '4/5', icon: UsersIcon, progress: 80, goal: 5 },
      { label: 'Team Events', value: '2/3', icon: CalendarIcon, progress: 67, goal: 3 },
      { label: 'Network Growth', value: '15/20', icon: NetworkIcon, progress: 75, goal: 20 }
    ],
    weeklyGoal: { label: 'Events', current: 8, target: 10 },
    monthlyGoal: { label: 'Target Score', current: 92, target: 95 }
  },
  WQ: {
    score: 88,
    trend: '+4',
    metrics: [
      {
        label: 'Efficiency Score',
        value: 86,
        goal: 90,
        icon: TrendingUpIcon,
        color: methodistColors.blue,
        progress: 86,
      },
      {
        label: 'Documentation',
        value: 92,
        goal: 95,
        icon: FileTextIcon,
        color: methodistColors.purple,
        progress: 92,
      },
      {
        label: 'Time Saved',
        value: 84,
        goal: 90,
        icon: ClockIcon,
        color: methodistColors.green,
        progress: 84,
      }
    ],
    highlights: [
      { label: 'Templates Used', value: '12/15', icon: FileTextIcon, progress: 80, goal: 15 },
      { label: 'Optimizations', value: '8/10', icon: SettingsIcon, progress: 80, goal: 10 },
      { label: 'Time Saved', value: '4.5 hrs', icon: ClockIcon, progress: 90, goal: 5 }
    ],
    weeklyGoal: { label: 'Optimizations', current: 8, target: 10 },
    monthlyGoal: { label: 'Target Score', current: 88, target: 95 }
  }
};

const domainIcons = {
  AQ: BrainIcon,
  CQ: UsersIcon,
  WQ: BuildingIcon,
};

const activities = [
  { 
    id: 'mindful-breathing',
    title: 'Mindful Breathing', 
    description: 'A quick breathing exercise to reduce stress between patient visits.',
    duration: '3 min',
    category: 'Mindfulness',
    icon: ActivityIcon,
    progress: 60,
    domain: 'AQ' as DomainType
  },
  { 
    id: 'gratitude-journal',
    title: 'Gratitude Journal', 
    description: 'Record three things you are grateful for to improve mental wellbeing.',
    duration: '5 min',
    category: 'Reflection',
    icon: FileTextIcon,
    progress: 40,
    domain: 'AQ' as DomainType
  },
  { 
    id: 'quick-stretch',
    title: 'Quick Stretch', 
    description: 'Simple desk stretches to reduce physical tension during your workday.',
    duration: '2 min',
    category: 'Physical',
    icon: ActivityIcon,
    progress: 80,
    domain: 'AQ' as DomainType
  },
  { 
    id: 'diversity-workshop',
    title: 'Diversity Workshop', 
    description: 'Interactive session on cultural competence in healthcare settings.',
    duration: '45 min',
    category: 'Education',
    icon: UsersIcon,
    progress: 0,
    domain: 'CQ' as DomainType
  },
  { 
    id: 'team-building',
    title: 'Team Building', 
    description: 'Virtual team building activity to strengthen colleague relationships.',
    duration: '30 min',
    category: 'Community',
    icon: UsersIcon,
    progress: 0,
    domain: 'CQ' as DomainType
  },
  { 
    id: 'mentorship-session',
    title: 'Mentorship Session', 
    description: 'Connect with a mentor or mentee to share knowledge and experience.',
    duration: '20 min',
    category: 'Growth',
    icon: UserIcon,
    progress: 0,
    domain: 'CQ' as DomainType
  },
  { 
    id: 'workflow-optimization',
    title: 'Workflow Optimization', 
    description: 'Identify and improve inefficient processes in your clinical workflow.',
    duration: '15 min',
    category: 'Efficiency',
    icon: SettingsIcon,
    progress: 0,
    domain: 'WQ' as DomainType
  },
  { 
    id: 'documentation-templates',
    title: 'Documentation Templates', 
    description: 'Create and share templates to streamline clinical documentation.',
    duration: '10 min',
    category: 'Organization',
    icon: FileTextIcon,
    progress: 0,
    domain: 'WQ' as DomainType
  },
  { 
    id: 'time-tracking',
    title: 'Time Tracking', 
    description: 'Analyze where your time is spent to identify opportunities for improvement.',
    duration: '5 min',
    category: 'Analysis',
    icon: ClockIcon,
    progress: 0,
    domain: 'WQ' as DomainType
  },
];

const UnifiedMobilePlatform: FC<UnifiedMobilePlatformProps> = ({
  activeDomain,
  defaultTab,
  highlightedFeature,
}) => {
  const [activeTab, setActiveTab] = useState(defaultTab || 'home');
  const [selectedActivity, setSelectedActivity] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null);

  const handleActivitySelect = (activityId: string) => {
    setSelectedActivity(activityId);
  };

  const handleBackFromActivity = () => {
    setSelectedActivity(null);
  };

  const handleEventSelect = (eventId: string) => {
    setSelectedEvent(eventId);
  };

  const handleBackFromEvent = () => {
    setSelectedEvent(null);
  };

  // Header for all views
  const Header = () => (
    <Box p={4} bg={methodistColors.blue} color="white">
      <HStack spacing={2}>
        <Icon as={domainIcons[activeDomain as DomainType]} />
        <Text fontSize="sm" fontWeight="medium">
          {domainTitles[activeDomain as DomainType]}
        </Text>
      </HStack>
    </Box>
  );

  // Prepare data for the unified home page
  const prepareUnifiedHomeData = () => {
    // Transform metrics for the unified home page
    const allMetrics = Object.entries(domainMetrics).flatMap(([domain, data]) => 
      data.metrics.map(metric => ({
        ...metric,
        domain: domain as 'AQ' | 'CQ' | 'WQ'
      }))
    );

    // Transform highlights for the unified home page
    const allHighlights = Object.entries(domainMetrics).flatMap(([domain, data]) => 
      data.highlights.map(highlight => ({
        ...highlight,
        domain: domain as 'AQ' | 'CQ' | 'WQ'
      }))
    );

    // Transform goals for the unified home page
    const allGoals = Object.entries(domainMetrics).flatMap(([domain, data]) => [
      {
        ...data.weeklyGoal,
        domain: domain as 'AQ' | 'CQ' | 'WQ'
      },
      {
        ...data.monthlyGoal,
        domain: domain as 'AQ' | 'CQ' | 'WQ'
      }
    ]);

    // Create domain-specific actions - streamlined to reduce redundancy
    const allActions = [
      // Core domain-specific actions
      { 
        name: 'Start Activity', 
        icon: ActivityIcon, 
        color: 'blue.500', 
        link: 'activities',
        domain: 'AQ' as 'AQ' | 'CQ' | 'WQ'
      },
      { 
        name: 'Join Event', 
        icon: UsersIcon, 
        color: 'blue.500', 
        link: 'activities',
        domain: 'CQ' as 'AQ' | 'CQ' | 'WQ'
      },
      { 
        name: 'Optimize Workflow', 
        icon: SettingsIcon, 
        color: 'blue.500', 
        link: 'activities',
        activityId: 'workflow-optimization',
        domain: 'WQ' as 'AQ' | 'CQ' | 'WQ'
      },
      // Metrics actions - one per domain
      { 
        name: 'Resilience Metrics', 
        icon: BarChart2Icon, 
        color: 'green.500', 
        link: 'metrics',
        domain: 'AQ' as 'AQ' | 'CQ' | 'WQ'
      },
      { 
        name: 'Culture Metrics', 
        icon: BarChart2Icon, 
        color: 'purple.500', 
        link: 'metrics',
        domain: 'CQ' as 'AQ' | 'CQ' | 'WQ'
      },
      { 
        name: 'Efficiency Metrics', 
        icon: BarChart2Icon, 
        color: 'blue.500', 
        link: 'metrics',
        domain: 'WQ' as 'AQ' | 'CQ' | 'WQ'
      },
      // Single unified assistant for all domains
      { 
        name: 'JIMI Assistant', 
        icon: MessageCircleIcon, 
        color: 'purple.500', 
        link: 'assistant',
        domain: 'ALL' as any // Make this available in all domains
      },
    ];

    // Create domain scores
    const domainScoresData = Object.entries(domainMetrics).map(([domain, data]) => ({
      domain: domain as 'AQ' | 'CQ' | 'WQ',
      score: data.score,
      trend: data.trend,
      label: domain,
      title: domain === 'AQ' ? 'Adaptability' : domain === 'CQ' ? 'Culture' : 'Workplace',
      icon: domainIcons[domain as 'AQ' | 'CQ' | 'WQ'],
      color: domain === 'AQ' ? methodistColors.green : 
             domain === 'CQ' ? methodistColors.purple : 
             methodistColors.blue,
      insight: domain === 'AQ' ? 
               "Your resilience is improving! Continue with daily mindfulness practices to maintain momentum." :
               domain === 'CQ' ? 
               "Your cultural engagement is increasing! Continue participating in community events to maintain your momentum." :
               "Your workplace efficiency is trending upward! Continue optimizing your workflows to save more time."
    }));

    return {
      metrics: allMetrics,
      highlights: allHighlights,
      goals: allGoals,
      actions: allActions,
      domainScores: domainScoresData
    };
  };

  const handleNavigate = (tab: string, domain?: 'AQ' | 'CQ' | 'WQ') => {
    setActiveTab(tab);
  };

  const renderContent = () => {
    if (selectedActivity) {
      return (
        <MobileResilienceActivityDetail 
          activityId={selectedActivity} 
          onBack={handleBackFromActivity} 
        />
      );
    }

    if (selectedEvent) {
      return (
        <MobileCultureEventDetail 
          eventId={selectedEvent} 
          onBack={handleBackFromEvent} 
        />
      );
    }

    const domainData = domainMetrics[activeDomain as DomainType];

    switch (activeTab) {
      case 'home':
        return (
          <UnifiedHomePage 
            {...prepareUnifiedHomeData()}
            onNavigate={handleNavigate}
          />
        );
      case 'activities':
        return activeDomain === 'CQ' 
          ? <MobileCultureActivities onSelectActivity={handleActivitySelect} activities={activities.filter(a => a.domain === 'CQ')} />
          : <MobileResilienceActivities onSelectActivity={handleActivitySelect} activities={activities.filter(a => a.domain === activeDomain as DomainType)} />;
      case 'metrics':
        return activeDomain === 'CQ'
          ? <MobileCultureMetrics 
              metrics={domainData.metrics}
              score={domainData.score}
              trend={domainData.trend}
              onNavigate={setActiveTab}
            />
          : <MobileResilienceMetrics 
              metrics={domainData.metrics}
              score={domainData.score}
              trend={domainData.trend}
              onNavigate={setActiveTab}
            />;
      case 'assistant':
        return activeDomain === 'CQ'
          ? <MobileCultureAssistant />
          : <MobileResilienceAssistant />;
      case 'wellness':
        return (
          <MobileCultureWellness 
            onNavigate={setActiveTab}
            onSelectEvent={handleEventSelect}
          />
        );
      case 'menu':
        return (
          <MobileMenu onNavigate={setActiveTab} activeDomain={activeDomain as 'AQ' | 'CQ' | 'WQ'} />
        );
      default:
        return <UnifiedHomePage 
          {...prepareUnifiedHomeData()}
          onNavigate={handleNavigate}
        />;
    }
  };

  return (
    <MobileFrame activeTab={activeTab} onTabChange={setActiveTab}>
      {renderContent()}
    </MobileFrame>
  );
};

export default UnifiedMobilePlatform;
